/*
 * @Author: Alex
 * @LastEditors: Alex yxfacw@163.com
 * @Date: 2023-05-25 13:17:36
 * @Description: swipe tool 地图卷帘工具
 */
import { BaseMapTool } from './mapToolBase'
import { Map } from 'ol'
import Control from 'ol/control/Control'
import { Layer } from 'ol/layer'
import { EventsKey } from 'ol/events'
import { unByKey } from 'ol/Observable'
import { getRenderPixel } from 'ol/render'
import BaseEvent from 'ol/events/Event'

interface MapSwipeEvent {
  [_: string]: any
}

export default class MapSwipeTool extends BaseMapTool<MapSwipeEvent> {
  /**
   * 进行 swipe 的目标图层
   */
  private _targetLayer: Layer

  /**
   * true： 自动添加图层到 map 的 top layer
   */
  private _autoAdd2Top = false

  private _prerenderListener: EventsKey | null = null

  private _postrenderListener: EventsKey | null = null

  /**
   * 用于显示在地图上可拖动的控件
   */
  private _swipeControl: MapSwipeControl | null = null

  constructor(options: { map: Map; target: Layer; autoAdd2Top?: boolean }) {
    super(options.map)
    this._targetLayer = options.target
    this._autoAdd2Top = options.autoAdd2Top ?? true
  }

  protected startInternal(): void {
    this._swipeControl = new MapSwipeControl()
    this.map.addControl(this._swipeControl)
    this._prerenderListener = this._targetLayer.on('prerender', event => {
      const ctx = event.context as CanvasRenderingContext2D
      if (ctx) {
        const mapSize = this.map.getSize()
        if (mapSize) {
          const width = this._swipeControl?.width ?? mapSize[0] / 2
          const tl = getRenderPixel(event, [width, 0])
          const tr = getRenderPixel(event, [mapSize[0], 0])
          const bl = getRenderPixel(event, [width, mapSize[1]])
          const br = getRenderPixel(event, mapSize)

          ctx.save()
          ctx.beginPath()
          ctx.moveTo(tl[0], tl[1])
          ctx.lineTo(bl[0], bl[1])
          ctx.lineTo(br[0], br[1])
          ctx.lineTo(tr[0], tr[1])
          ctx.closePath()
          ctx.clip()
        }
      }
    })

    // after rendering the layer, restore the canvas context
    this._postrenderListener = this._targetLayer.on(
      'postrender',
      function (event) {
        const ctx = event.context as CanvasRenderingContext2D
        ctx.restore()
      }
    )
    if (this._autoAdd2Top) {
      // 这里将图层移动到最上层 否则无法显示
      this.map.removeLayer(this._targetLayer)
      this.map.addLayer(this._targetLayer)
      this._targetLayer.setVisible(true)
    }
  }
  protected stopInternal(): void {
    if (this._prerenderListener) {
      unByKey(this._prerenderListener)
    }
    if (this._postrenderListener) {
      unByKey(this._postrenderListener)
    }
    if (this._autoAdd2Top) {
      this._targetLayer.setVisible(false)
      this.map.removeLayer(this._targetLayer)
    }
    if (this._swipeControl) {
      this.map.removeControl(this._swipeControl)
      this._swipeControl = null
    }
  }
}

class MapSwipeControl extends Control {
  private _isMoving = false
  private _width: number | undefined
  constructor(options?: any) {
    options = options || {}

    const button = document.createElement('button')
    const element = document.createElement('div')
    element.className =
      (options.className || 'ol-swipe') + ' ol-unselectable ol-control'
    super({
      element: element
    })

    element.appendChild(button)
    element.addEventListener('mousedown', evt => this.move(evt))
    element.addEventListener('touchstart', evt => this.move(evt))

    this.on('propertychange', e => {
      if (this.get('orientation') === 'horizontal') {
        this.element.style.top = this.get('position') * 100 + '%'
        this.element.style.left = ''
      } else {
        if (this.get('orientation') !== 'vertical')
          this.set('orientation', 'vertical')
        this.element.style.left = this.get('position') * 100 + '%'
        this.element.style.top = ''
      }
      if (e.key === 'orientation') {
        this.element.classList.remove('horizontal', 'vertical')
        this.element.classList.add(this.get('orientation'))
      }

      this.getMap()?.render()
    })
    this.on('change', evt => {
      this.getMap()?.render()
    })

    this.set('position', options.position || 0.5)
    this.set('orientation', options.orientation || 'vertical')
    this.set('right', options.right)
  }

  get width() {
    return this._width
  }

  private move(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
      const evt: MouseEvent = e as MouseEvent
      switch (evt.type) {
        case 'mouseup':
          {
            this._isMoving = false
            document.removeEventListener('mouseup', evt => this.move)
            document.removeEventListener('mousemove', evt => this.move)
          }

          break
        case 'mousedown':
          {
            this._isMoving = true
            document.addEventListener('mouseup', evt => this.move(evt))
            document.addEventListener('mousemove', evt => this.move(evt))
          }
          break
        case 'mousemove':
          {
            const ele = this.getMap()?.getTargetElement()
            const size = this.getMap()?.getSize()
            if (this._isMoving && ele && size) {
              if (this.get('orientation') === 'vertical') {
                let pageX = evt.pageX
                if (!pageX) break
                pageX -=
                  ele?.getBoundingClientRect().left +
                  window.pageXOffset -
                  document.documentElement.clientLeft

                let l = size[0]
                const w = l - Math.min(Math.max(0, l - pageX), l)
                l = w / l
                this.set('position', l)
                this.dispatchEvent(
                  new SwipMovingEvent({
                    size: [w, size[1]],
                    position: [l, 0]
                  })
                )
                // testme
                this._width = w
              } else {
                let pageY = evt.pageY
                if (!pageY) break
                pageY -=
                  ele.getBoundingClientRect().top +
                  window.pageYOffset -
                  document.documentElement.clientTop

                let l = size[1]
                const h = l - Math.min(Math.max(0, l - pageY), l)
                l = h / l
                this.set('position', l)

                this.dispatchEvent(
                  new SwipMovingEvent({
                    size: [size[0], h],
                    position: [0, l]
                  })
                )

                // testme
                this._width = size[0]
              }
            }
          }
          break
      }
    } else {
      // const evt: TouchEvent = e as TouchEvent
      console.error('Not Impled..')
    }
  }
}

class SwipMovingEvent extends BaseEvent {
  private _options: { size: number[]; position: number[] }
  constructor(options: { size: number[]; position: number[] }) {
    super('moving')
    this._options = options
  }

  get options() {
    return this._options
  }
}
